import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import firebase from "../../../firebase/compat";
import { db } from "../../../firebase/config";
import { client } from "../../../hooks/Client";
import Table from "../../common/table/Table.jsx";
import TableCell from "../../common/table/TableCell.jsx";
import { Link } from "react-router-dom";
import presenceReportHours from "../../../helpers/personas/presenceReportHours";
import notDeleted from "../../../helpers/notDeleted";

export const MONTH_OPTIONS = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December",
];

export const YEAR_OPTIONS = [2023, 2024, 2025, 2026, 2027];

const PresenceTotals = ({ personas, month, year, presenceReports: reportsProp }) => {
  const isControlled = reportsProp !== undefined;

  // States for selected month and year (only used in uncontrolled mode).
  const [internalMonth, setInternalMonth] = useState(new Date().getMonth() + 1);
  const [internalYear, setInternalYear] = useState(new Date().getFullYear());
  const [internalReports, setInternalReports] = useState([]);
  const [row1Height, setRow1Height] = useState(37);
  const tableWrapperRef = useRef(null);

  const presenceMonth = month ?? internalMonth;
  const presenceYear = year ?? internalYear;
  const presenceReports = isControlled ? reportsProp : internalReports;

  // Firestore data — only fetch when uncontrolled (parent may own reports).
  useEffect(() => {
    if (isControlled) return;

    const startDate = new Date(presenceYear, presenceMonth - 1, 1);
    const endDate = new Date(presenceYear, presenceMonth, 0, 23, 59, 59);
    const start = firebase.firestore.Timestamp.fromDate(startDate);
    const end = firebase.firestore.Timestamp.fromDate(endDate);

    db.collection("PersonaPresenceReports")
      .where("CompagnyID", "==", client)
      .where("Timestamp", ">=", start)
      .where("Timestamp", "<=", end)
      .get()
      .then((snapshot) => {
        // Een rapport in de prullenbak telt niet mee (ADR-008).
        setInternalReports(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })).filter(notDeleted));
      });
  }, [presenceMonth, presenceYear, isControlled]);

  // Measure the actual height of the first header row so row-2 top offset is
  // exact. Deps limited so this only runs when the header structure can change.
  useLayoutEffect(() => {
    if (!tableWrapperRef.current) return;
    const firstRow = tableWrapperRef.current.querySelector("thead tr:first-child");
    if (!firstRow) return;
    const measured = firstRow.getBoundingClientRect().height;
    setRow1Height((prev) => (prev === measured ? prev : measured));
  }, [presenceMonth, presenceYear, personas]);

  // Handlers for dropdown changes (uncontrolled mode only).
  const onChangeMonth = (e) => {
    setInternalMonth(parseInt(e.target.value));
  };

  const onChangeYear = (e) => {
    setInternalYear(parseInt(e.target.value));
  };

  // Calculate number of days in the selected month.
  const daysInSelectedMonth = useMemo(() => {
    return new Date(presenceYear, presenceMonth, 0).getDate();
  }, [presenceYear, presenceMonth]);

  // Build an O(1) lookup: "PersonaID-day" → report. Avoids an N×D×R
  // find() when rendering cells (20 personas × 31 days × 200 reports ≈
  // 124k comparisons per render otherwise).
  const reportsByKey = useMemo(() => {
    const map = new Map();
    for (const report of presenceReports) {
      if (!report.Timestamp) continue;
      const rDate = report.Timestamp.toDate();
      if (rDate.getFullYear() !== presenceYear) continue;
      if (rDate.getMonth() + 1 !== presenceMonth) continue;
      map.set(`${report.PersonaID}-${rDate.getDate()}`, report);
    }
    return map;
  }, [presenceReports, presenceYear, presenceMonth]);

  // Helper: returns abbreviated day-of-week in Dutch.
  const getDayAbbreviation = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    const days = ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"];
    return days[date.getDay()];
  };

  // Helper: true if the given day in the selected month falls on Sat/Sun.
  const isWeekend = (day) => {
    const weekday = new Date(presenceYear, presenceMonth - 1, day).getDay();
    return weekday === 0 || weekday === 6;
  };

  // Helper: returns the report summary for a given persona + day.
  //
  // Het rekenwerk staat in `presenceReportHours`, samen met de jaarweergave en
  // de deelnemerspagina. Dit stond hier eerder los, en die kopie las een
  // ontbrekende `CorrectedStart` als een uitzondering en zette de dag daardoor
  // op 0.0 uur — terwijl dezelfde dag op de deelnemerspagina gewoon 8 uur was.
  // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
  const getDayReport = (personaID, day) => {
    const report = reportsByKey.get(`${personaID}-${day}`);
    if (!report) return { text: "", type: null, reportID: null };

    const { hours, type } = presenceReportHours(report);

    return { text: hours.toFixed(1), type, reportID: report.ID };
  };

  // Helper: sums the total hours from numeric reports for the month.
  const getMonthlyTotal = (personaID) => {
    let total = 0;
    for (let d = 1; d <= daysInSelectedMonth; d++) {
      const { text } = getDayReport(personaID, d);
      const parsed = parseFloat(text);
      if (!isNaN(parsed)) {
        total += parsed;
      }
    }
    return total;
  };

  return (
    <div className="home-section-container">

      <div>
        {!isControlled && (
          <>
            <select value={presenceMonth} onChange={onChangeMonth}>
              {MONTH_OPTIONS.map((label, index) => (
                <option key={label} value={index + 1}>{label}</option>
              ))}
            </select>

            <select value={presenceYear} onChange={onChangeYear}>
              {YEAR_OPTIONS.map((yr) => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </>
        )}

        {/* Inline CSS custom property: needed because --row-1-height is a
            measured pixel value that can't be known at stylesheet write time.
            This is the documented exception to CLAUDE.md rule #3. */}
        <div ref={tableWrapperRef} style={{ "--row-1-height": `${row1Height}px` }}>
        <Table
          outerClassName="presence-totals-table__outer"
          containerClassName="presence-totals-table__container"
          tableClassName="presence-totals-table"
          columns={[]}
          headerRows={[
            [
              { key: "name", label: "Naam", rowSpan: 2, headerClassName: "common-table__head-cell--sticky-1" },
              { key: "total", label: "Totaal", rowSpan: 2, headerClassName: "common-table__head-cell--sticky-2" },
              ...[...Array(daysInSelectedMonth)].map((_, dayIndex) => {
                const dayNum = dayIndex + 1;
                const weekend = isWeekend(dayNum);
                return {
                  key: `day-${dayNum}`,
                  label: `${dayNum}`,
                  headerClassName: weekend ? "presence-totals-table__head-cell--weekend" : "",
                };
              }),
            ],
            [
              ...[...Array(daysInSelectedMonth)].map((_, dayIndex) => {
                const dayNum = dayIndex + 1;
                const weekend = isWeekend(dayNum);
                return {
                  key: `weekday-${dayNum}`,
                  label: getDayAbbreviation(presenceYear, presenceMonth, dayNum),
                  headerClassName: `common-table__head-cell--row-2${weekend ? " presence-totals-table__head-cell--weekend" : ""}`,
                };
              }),
            ],
          ]}
          items={personas || []}
          getItemKey={(persona) => persona.ID}
          renderRow={(persona) => {
            const monthlyTotal = getMonthlyTotal(persona.ID);

            return (
              <>
                <TableCell className="common-table__cell--sticky-1">{persona.Name}</TableCell>
                <TableCell className="common-table__cell--sticky-2">{monthlyTotal ? monthlyTotal.toFixed(1) : 0}&nbsp;</TableCell>
                {[...Array(daysInSelectedMonth)].map((_, dayIndex) => {
                  const dayNum = dayIndex + 1;
                  const dayReport = getDayReport(persona.ID, dayNum);
                  const weekend = isWeekend(dayNum);
                  const cellClass = [
                    weekend && "presence-totals-table__cell--weekend",
                    dayReport.type && `presence-totals-table__cell--${dayReport.type}`,
                  ].filter(Boolean).join(" ");

                  return (
                    <TableCell key={`p-${persona.ID}-d-${dayNum}`} className={cellClass}>
                      {dayReport.reportID ? (
                        <Link to={`/${client}/personapresencereportdetail/${persona.ID}/${dayReport.reportID}`}>
                          {dayReport.text}
                        </Link>
                      ) : dayReport.text}
                    </TableCell>
                  );
                })}
              </>
            );
          }}
          showSearch={false}
          emptyMessage="Geen aanwezigheidsgegevens gevonden."
        />
        </div>

        {/* Legend */}
        <div className="presence-totals-legend">
          <span className="presence-totals-legend__item">
            <span className="presence-totals-legend__dot presence-totals-legend__dot--correct" />
            Correct
          </span>
          <span className="presence-totals-legend__item">
            <span className="presence-totals-legend__dot presence-totals-legend__dot--correction" />
            Aangepast
          </span>
          <span className="presence-totals-legend__item">
            <span className="presence-totals-legend__dot presence-totals-legend__dot--absent" />
            Afwezig
          </span>
        </div>
      </div>
    </div>
  );
};

export default PresenceTotals;
